import { useEffect, useState } from 'react'
import { FC } from 'react'
import { useForm } from 'react-hook-form'
import { FormProvider } from 'react-hook-form'
import { useActions, useAppSelector } from 'store/hooks'
import { LoaderInCentalPosition } from 'widget'

import { AddressPopup } from './components/AddressPopup/AddressPopup'
import { useSetAddressError } from './hooks/useSetAddressError'
import { selectAddress } from './redux/addressCreateSelector'
import {
  SavedAddress,
  useAddAddressMutation,
  useChangeAddressMutation,
  useGetAddressListQuery,
  useRemoveAddressMutation,
} from './redux/MyAddresses.api'
import { AddressErrorsList } from './types'
import { AddressesList } from './view/AddressesList'
import { AddressSelectForm } from './view/AddressSelectForm'

// страница "/myaddresses"
export const MyAddresses: FC = () => {
  const [viewMode, setViewMode] = useState<string>('list')
  const { data: addressesData, isLoading: isLoadingAllData } =
    useGetAddressListQuery()
  const [currentEdit, setCurrentEdit] = useState<null | SavedAddress>(null)
  const methods = useForm({ defaultValues: { address: currentEdit } })

  const {
    setInputAddress,
    setClickMarkCoords,
    setCenter,
    clearAddress,
    clearMap,
    setInputAddressSuggest,
  } = useActions()

  const { suggests, inputSuggest } = useAppSelector(selectAddress)
  const [isShowLoader, setShowLoader] = useState<boolean>(false)

  const [addAddress, { isLoading: isLoadingAdding, isSuccess }] =
    useAddAddressMutation()
  const [changeAddress, { isLoading: isLoadingChanging }] =
    useChangeAddressMutation()
  const [removeAddress] = useRemoveAddressMutation()
  const isLoading = isLoadingChanging || isLoadingAdding
  const [isLoadingId, setIsLoadingId] = useState<number | null>(null)
  const setAddressError = useSetAddressError()

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingId(null)
    }
  }, [isLoading])

  // чистим карту и адрес при инициализации компонента
  useEffect(() => {
    clearAddress()
    clearMap()
  }, [])

  // добавляю прелоадер при добавлении нового адреса
  useEffect(() => {
    if (isLoadingAdding) {
      setShowLoader(true)
    }

    if (isSuccess) {
      setTimeout(() => {
        setShowLoader(false)
      }, 1000)
    }
  }, [isSuccess, isLoadingAdding])

  // сохраняем изменения адреса
  const saveChangesAddress = (current: SavedAddress | null) => {
    const isAddressAlreadyExist = addressesData?.list.some(
      address => address?.fiasId === inputSuggest?.fiasId,
    )

    if (isAddressAlreadyExist) {
      setAddressError(AddressErrorsList.addressAlreadyExist)
      return
    }

    if (inputSuggest === null) {
      return
    }

    if (!current) {
      setIsLoadingId(0)
      addAddress(inputSuggest)
    } else {
      setIsLoadingId(current.id)
      changeAddress({
        id: current.id,
        body: inputSuggest,
      })
    }
    setViewMode('list')
  }

  const removeAddressHandler = (current: SavedAddress) => {
    setIsLoadingId(current.id)
    removeAddress(current.id)
    setViewMode('list')
  }

  // переключаем отображаемый компонент на форму
  const setViewModeToForm = (item: SavedAddress | null) => {
    // проверяем новый адрес или редактирование
    if (item && suggests) {
      setInputAddress(item.address)
      setInputAddressSuggest(item)
      setClickMarkCoords([Number(item.geoLat), Number(item.geoLon)])
      setCenter([Number(item.geoLat), Number(item.geoLon)])
    } else {
      clearAddress()
      clearMap()
      setClickMarkCoords(null)
    }
    setCurrentEdit(item)
    setViewMode('form')
  }

  if (isLoadingAllData || isShowLoader) return <LoaderInCentalPosition />
  if (!addressesData) return <></>

  switch (viewMode) {
    // форма добавления или изменения адреса
    case 'form':
      return (
        <FormProvider {...methods}>
          <AddressSelectForm
            setViewMode={setViewMode}
            saveChangesAddress={saveChangesAddress}
            currentEdit={currentEdit}
            removeAddress={removeAddressHandler}
          />
        </FormProvider>
      )
    // выбор адреса без карты (для мобилки)
    case 'popup':
      return (
        <FormProvider {...methods}>
          <AddressPopup setViewMode={setViewMode} />
        </FormProvider>
      )
    // список "МОИ АДРЕСА"
    default:
      return (
        <>
          <AddressesList
            addressesData={addressesData}
            isLoadingId={isLoadingId}
            isLoadingAdding={isLoadingAdding}
            setViewModeToForm={setViewModeToForm}
          />
        </>
      )
  }
}
